import './credit-card-inputs.scss';
import { useState, useEffect } from 'react';
import { observer, inject } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { useSortableTableData } from '../../components/table/hooks/useSortableTableData';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import '../credit-card-transactions/credit-card-transactions.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAutoRefresh, useStatementData } from './hooks';
import DropdownMenu from '../../components/dropdown-menu/dropdown-menu';
import UncategorizedExpensesTable from './uncategorized-expenses-table/uncategorized-expenses-table';
import Toggle from '../../components/toggle/toggle';
import './credit-card-statements.scss';
import { statementTablePaperSx, statementTableContainerSx, statementTableSx, statementTableHeaderSx } from '../../components/table/mui-table-styles';

const columnNameKeys = {
  start_date: 'cc_statements.statement_imports.table.period',
  card_label: 'cc_statements.statement_imports.table.card',
  status: 'cc_statements.column_names.status',
  total_with_tax: 'cc_statements.column_names.total_with_tax',
  paired_transactions: 'cc_statements.column_names.paired_transactions',
  unmatched_expenses: 'cc_statements.column_names.unmatched_expenses',
  currency_code: 'currency',
};

export const adminRoles = ['Admin', 'Manager', 'SuperAdmin'];

const FILTER_STORAGE_KEY = 'creditCardStatementsFilter';
const defaultFilters = { accountMask: 'all', year: 'all', month: 'all' };

const loadFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw);
    return { ...defaultFilters, ...parsed };
  } catch {
    return defaultFilters;
  }
};

const persistFilters = (filters) => {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
};

const CreditCardTransactions = ({ data }) => {
  useLanguageChange(data.t);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userCanSeeAdminData = adminRoles.includes(data.role || '');
  const [accountMasks, setAccountMasks] = useState([]);
  const [storedFilters] = useState(() => loadFiltersFromStorage());
  const [selectedAccountMask, setSelectedAccountMask] = useState(searchParams.get('accountMask') || storedFilters.accountMask);
  const [selectedYear, setSelectedYear] = useState(storedFilters.year);
  const [selectedMonth, setSelectedMonth] = useState(storedFilters.month);
  const [hideCompletedStatements, setHideCompletedStatements] = useState(false);
  const { refreshing, refreshData } = useAutoRefresh();
  const { statements, fetchStatements, loading: statementsLoading, years } = useStatementData({ t: data.t, setAccountMasks });
  
  // Keep the existing three-step sort cycle when rendering with MUI.
  const { sortedData, sortConfig, handleSort } = useSortableTableData(statements);
  const columns = Object.entries(columnNameKeys);

  const months = [
      {value: 1, label: data.t.read('month.January')},
      {value: 2, label: data.t.read('month.February')},
      {value: 3, label: data.t.read('month.March')},
      {value: 4, label: data.t.read('month.April')},
      {value: 5, label: data.t.read('month.May')},
      {value: 6, label: data.t.read('month.June')},
      {value: 7, label: data.t.read('month.July')},
      {value: 8, label: data.t.read('month.August')},
      {value: 9, label: data.t.read('month.September')},
      {value: 10, label: data.t.read('month.October')},
      {value: 11, label: data.t.read('month.November')},
      {value: 12, label: data.t.read('month.December')}
  ]
  useEffect(() => {
    const accountMaskFilter = selectedAccountMask && selectedAccountMask !== 'all' ? selectedAccountMask : '';

    const yearFilter = selectedYear && selectedYear !== 'all' ? selectedYear : '';
    
    const monthFilter = selectedMonth && selectedMonth !== 'all' ? selectedMonth : '';
    
    const sorting = [{ id: 'start_date', desc: true }];
    let filters = [];
    if (accountMaskFilter) {
      filters.push({ id: 'account_mask', value: accountMaskFilter });
    }
    if (yearFilter) {
      filters.push({ id: 'year', value: Number(yearFilter) });
    }
    if (monthFilter) {
        filters.push({ id: 'period', value: Number(monthFilter) });
    }
    
    fetchStatements({ sorting, filters, hideCompletedStatements });
  }, [selectedYear, selectedAccountMask, selectedMonth, hideCompletedStatements, data.t, data.role, data.origin_role]);

  useEffect(() => {
    persistFilters({
      accountMask: selectedAccountMask,
      year: selectedYear,
      month: selectedMonth,
    });
  }, [selectedAccountMask, selectedYear, selectedMonth]);

  const handleRowClick = async (row) => {
    // Pass the account mask filter to the transaction page if one is selected
    if (selectedAccountMask && selectedAccountMask !== 'all') {
      navigate(`/c_card/statement/${row._id}?accountMask=${selectedAccountMask}`);
    } else {
      navigate(`/c_card/statement/${row._id}`);
    }
  };

  const handleAccountMaskChange = (e) => {
    const newAccountMask = e.target.value;
    setSelectedAccountMask(newAccountMask);
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (newAccountMask === 'all') {
      newSearchParams.delete('accountMask');
    } else {
      newSearchParams.set('accountMask', newAccountMask);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <div className='card p-1 mt-2 rounded-lg transaction-card credit-card-inputs'>
      <div className='tr-top-bar'>
        <div className='credit-transactions-heading ml-4 mt-4'>
          <h2 className='m-0'>{data.t.read('cc_statements.title')}</h2>
        </div>
        <div className='mr-2'>
          <DropdownMenu refreshing={refreshing}>
            <button
              className='dropdown-item d-flex align-items-center'
              onClick={async () => {
                refreshData().then((response) => {
                  if (response.data.message === 'ok') {
                    fetchStatements();
                  } else {
                    toast.error(data.t.read('cc_transactions.refresh_error'));
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_transactions.refresh_data')}
            </button>
          </DropdownMenu>
        </div>
      </div>
      <div className='card-body'>
        <div className='statement-filters'>
          <div className='statement-filters__field'>
            <label htmlFor='account_mask' className='form-label'>
              {data.t.read('cc_statements.column_names.card_number')}
            </label>
            <select className='form-control' id='account_mask' value={selectedAccountMask} onChange={handleAccountMaskChange}>
              <option value='all'>{data.t.read('cc_statements.all_cards')}</option>
              {accountMasks.map((mask) => (
                <option key={mask} value={mask}>
                  **** **** **** {mask}
                </option>
              ))}
            </select>
          </div>

          <div className='statement-filters__field'>
            <label htmlFor='statement-year' className='form-label'>
              {data.t.read('year')}
            </label>
            <select className='form-control' id='statement-year' value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
              <option value='all'>{data.t.read('cc_statements.all_years')}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

            <div className='statement-filters__field'>
                <label htmlFor='statement-month' className='form-label'>
                    {data.t.read('monthly')}
                </label>
                <select className='form-control' id='statement-month' value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                    <option value='all'>{data.t.read('cc_statements.all_months')}</option>
                    {months.map((month) => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>
            </div>
            
          <div className='statement-filters__toggle'>
            <Toggle
              id='show-incomplete-statements-toggle'
              isOn={hideCompletedStatements}
              handleToggle={() => setHideCompletedStatements(!hideCompletedStatements)}
              label={data.t.read('cc_statements.isolate_incomplete_statements')}
            />
          </div>
          <button
            type='button'
            className='statement-filters__reset'
            onClick={() => {
              // Reuse the card handler so reset also clears its URL filter.
              handleAccountMaskChange({ target: { value: 'all' } });
              setSelectedYear('all');
              setSelectedMonth('all');
              setHideCompletedStatements(false);
            }}
          >
            {data.t.read('reset_filters')}
          </button>
        </div>
        <Box component='h2' sx={{ m: 0, mb: 2.5, fontSize: '18px', fontWeight: 500, color: '#20242a' }}>
          {data.t.read('statements')}
        </Box>
        <Paper elevation={0} sx={{ ...statementTablePaperSx, mb: 5 }}>
          <TableContainer sx={statementTableContainerSx}>
            <Table
              stickyHeader
              aria-label={data.t.read('statements_account_list')}
              aria-busy={statementsLoading}
              sx={statementTableSx}
            >
              <TableHead>
                <TableRow>
                  {columns.map(([key, label]) => (
                    <TableCell
                      key={key}
                      sortDirection={sortConfig.key === key ? sortConfig.direction : false}
                      sx={statementTableHeaderSx}
                    >
                      <TableSortLabel
                        active={sortConfig.key === key}
                        direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort(key)}
                      >
                        {data.t.read(label)}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {statementsLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length}>
                      <div className='d-flex justify-content-center'><LoadingSpinner /></div>
                    </TableCell>
                  </TableRow>
                ) : sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align='center'>{data.t.read('no_data')}</TableCell>
                  </TableRow>
                ) : sortedData.map((row) => (
                  <TableRow
                    key={row._id}
                    hover
                    tabIndex={0}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row)}
                    onKeyDown={(event) => {
                      // Let keyboard users open the same statement as a mouse click.
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      handleRowClick(row);
                    }}
                  >
                    {columns.map(([key]) => (
                      <TableCell key={key}>
                        {key === 'start_date' ? (
                          // Keep the raw start date as the sort key while grouping the display fields.
                          <Box sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ fontWeight: 500 }}>{[row.period, row.year].filter(Boolean).join(' ')}</Box>
                            {(row.start_date || row.end_date) && (
                              <Box sx={{ mt: 0.5, fontSize: '12px', color: '#667085' }}>
                                {row.start_date || '—'} - {row.end_date || '—'}
                              </Box>
                            )}
                          </Box>
                        ) : key === 'card_label' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, whiteSpace: 'nowrap' }}>
                            <Box aria-hidden='true' sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              width: 36, height: 36, flexShrink: 0, borderRadius: '50%',
                              backgroundColor: '#f0f2f5', color: '#667085',
                            }}>
                              <FontAwesomeIcon icon={faCreditCard} />
                            </Box>
                            <Box>
                              <Box sx={{ fontWeight: 500 }}>{row.card_label}</Box>
                              {/* Combining columns must not expose details hidden from employees. */}
                              {userCanSeeAdminData && (
                                <Box sx={{ mt: 0.5, fontSize: '12px', color: '#667085' }}>
                                  {[
                                    row.account_mask?.match(/\d{4}$/) ? `•••• ${row.account_mask.slice(-4)}` : null,
                                    row.card_type,
                                  ].filter(Boolean).join(' · ')}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ) : row[key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <div className='table m-0 p-0'>
          <UncategorizedExpensesTable filteredMask={selectedAccountMask} refreshStatementData={fetchStatements} />
        </div>
      </div>
    </div>
  );
};

export default inject('data')(observer(CreditCardTransactions));
